import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { firstValueFrom, map } from 'rxjs';
import { environment } from 'src/environments/environment';
// Importación alineada a la ubicación real del dominio (ver skills y estructura hexagonal)
import { RespuestaLlamadaRepository, RespuestaLlamada, RespuestaLlamadaBackendResponse } from 'src/app/domain/service/respuesta-llamada/respuesta-llamada-repository';
import { DireccionCursor, PageResponse } from 'src/app/domain/model/common/page-response.dto';
import { validarSize } from 'src/app/common/validation-utils';

/**
 * Adaptador HTTP: HttpRespuestaLlamadaRepository
 *
 * Implementa el puerto RespuestaLlamadaRepository para comunicación con backend.
 * Realiza llamadas REST al RespuestaLlamadaController en servicios-ms.
 *
 * Migrado del legacy GWT UtilidadesBuscadores.buscarRespuesta().
 * Paginación keyset/cursor del servidor (size, cursor, direction).
 *
 * Arquitectura Hexagonal:
 * - Este adaptador está en infraestructura
 * - Implementa el puerto RespuestaLlamadaRepository del dominio
 * - Modelo y puerto ubicados en: domain/service/catalogo/respuestallamada/
 */
@Injectable({ providedIn: 'root' })
export class HttpRespuestaLlamadaRepository implements RespuestaLlamadaRepository {

  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.backend.baseUrl}/servicios/respuestas-llamada`;

  /**
   * Mapea respuesta del backend al modelo RespuestaLlamada del dominio.
   */
  private mapToRespuestaLlamada(response: RespuestaLlamadaBackendResponse): RespuestaLlamada {
    return {
      nmconsecutivo: response.nmconsecutivo,
      dsrespuesta: response.dsrespuesta,
      snAplicaNuevaLlamada: response.snAplicaNuevaLlamada || 'N',
      nmhorasNuevaLlamada: response.nmhorasNuevaLlamada || null
    };
  }

  /**
   * Página de respuestas de llamada (keyset/cursor, seguridad-girop §2.3).
   * Endpoint: GET /api/servicios/respuestas-llamada?filtro=F&size=N&direction=D[&cursor=C]
   */
  buscarPagina(
    filtro: string,
    cursor: string | null,
    size: number,
    direction: DireccionCursor
  ): Promise<PageResponse<RespuestaLlamada>> {
    validarSize(size);
    let params = new HttpParams()
      .set('size', String(size))
      .set('direction', direction);
    if (filtro?.trim()) params = params.set('filtro', filtro.trim());
    if (cursor) params = params.set('cursor', cursor);

    return firstValueFrom(
      this.http
        .get<PageResponse<RespuestaLlamadaBackendResponse>>(this.baseUrl, { params })
        .pipe(
          map((page) => ({
            ...page,
            content: (page?.content ?? []).map((item) => this.mapToRespuestaLlamada(item))
          }))
        )
    );
  }
}
